import { memo, useId, useState } from 'react';
import type { BalanceGroupDTO, BillDTO } from '@six-pockets/shared';
import { ChevronDownIcon, EyeIcon, EyeOffIcon } from '@/components/icons';
import { Avatar, Button } from '@/components/ui';
import { useReveal } from '@/hooks/useReveal';
import { cx } from '@/utils/cx';
import { formatDateTime, formatINR, formatPhone } from '@/utils/format';
import { billAge, billContents, billParty } from '../utils';
import styles from '@/styles/components/BalanceGroupCard/style.scss';

interface BalanceGroupCardProps {
  group: BalanceGroupDTO;
  /** True for logins without the reports module (the club manager): the due stays hidden until tapped or Details. */
  masked: boolean;
  onPay: (group: BalanceGroupDTO) => void;
  onView: (bill: BillDTO) => void;
}

/**
 * One customer's balance: who, how many bills and since when, the total due and Pay now. Details opens the bills
 * underneath (oldest first, the order a payment clears them), each with View for the full bill.
 */
export const BalanceGroupCard = memo(function BalanceGroupCard({ group, masked, onPay, onView }: BalanceGroupCardProps) {
  const [open, setOpen] = useState(false);
  const [revealed, toggleRevealed] = useReveal();
  const listId = useId();
  // Details shows the due while open.
  const dueShown = revealed || open;
  const { customer, bills } = group;
  const name = customer?.name ?? billParty(bills[0]!);
  const n = bills.length;

  return (
    <li className={cx(styles.card, open && styles.open)}>
      <div className={styles.summary}>
        <Avatar name={name} tone={customer ? 'green' : 'neutral'} />
        <div className={styles.who}>
          <span className={styles.name}>{name}</span>
          <span className={styles.meta}>
            {customer ? <span className={styles.phone}>{formatPhone(customer.phone)}</span> : <span>No customer · Bill #{bills[0]!.billNumber}</span>}
            <span>
              {n} {n === 1 ? 'bill' : 'bills'} · oldest {billAge(bills[0]!.createdAt)}
            </span>
          </span>
        </div>
        {masked ? (
          // Hidden from the club manager until tapped (as the revenue cards on Bills home); a second tap hides it.
          <button
            type="button"
            className={cx(styles.due, styles.dueButton)}
            aria-pressed={dueShown}
            aria-label={dueShown ? `Due ${formatINR(group.totalDue)}` : 'Due: hidden. Tap to show'}
            onClick={toggleRevealed}
          >
            <span className={styles.dueLabel}>
              Due {dueShown ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}
            </span>
            <span className={cx(styles.dueValue, !dueShown && styles.dueHidden)} aria-hidden>
              {dueShown ? formatINR(group.totalDue) : '₹ ••••'}
            </span>
          </button>
        ) : (
          <div className={styles.due}>
            <span className={styles.dueLabel}>Due</span>
            <span className={styles.dueValue}>{formatINR(group.totalDue)}</span>
          </div>
        )}
        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="md"
            aria-expanded={open}
            aria-controls={listId}
            icon={<ChevronDownIcon size={16} className={styles.chevron} />}
            onClick={() => setOpen((o) => !o)}
          >
            Details
          </Button>
          <Button variant="primary" size="md" onClick={() => onPay(group)}>
            Pay now
          </Button>
        </div>
      </div>

      {open && (
        <ul id={listId} className={styles.bills}>
          {bills.map((b) => (
            <li key={b.id} className={styles.bill}>
              <span className={styles.number}>#{b.billNumber}</span>
              <span className={styles.billMain}>
                <span className={styles.when}>{formatDateTime(b.createdAt)}</span>
                <span className={styles.contents}>{billContents(b)}</span>
              </span>
              <span className={styles.figures}>
                <span>
                  Total <b className={styles.total}>{formatINR(b.total)}</b>
                </span>
                {b.discount > 0 && (
                  <span>
                    Discount <b className={styles.discount}>{formatINR(b.discount)}</b>
                  </span>
                )}
                {b.paidAmount > 0 && (
                  <span>
                    Paid <b className={styles.paid}>{formatINR(b.paidAmount)}</b>
                  </span>
                )}
                {b.waived > 0 && (
                  <span>
                    Waived <b className={styles.discount}>{formatINR(b.waived)}</b>
                  </span>
                )}
                <span>
                  Due <b className={styles.billDue}>{formatINR(b.balanceDue)}</b>
                </span>
              </span>
              <Button size="sm" variant="secondary" icon={<EyeIcon size={15} />} onClick={() => onView(b)} aria-label={`View bill ${b.billNumber}`}>
                View
              </Button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
});
